// Canonical role-name groups used for route/UI access control across the app.
//
// These are the literal strings stored in MemberBasic.Role on the backend.
// Matching everywhere is case-insensitive and whitespace-trimmed (see
// hasRole() below and ProtectedRoute/PinProtectedRoute), so the exact
// casing here doesn't have to be perfect -- but the exact SPELLING does,
// since different spellings are different strings. Several legacy/duplicate
// variants (e.g. "Club Secretary" vs "secretary", the misspelled "Club
// Performence Evaluator") are kept because real member rows still use them;
// don't remove a variant without checking it's no longer assigned to anyone.

export const ROLES = {
  SYSTEM_ADMIN: 'System Admin',
  PRESIDENT: 'president',
  CLUB_PRESIDENT: 'Club President',
  PRESIDENT_TITLECASE: 'President',
  VICE_PRESIDENT: 'Vice President',
  SECRETARY: 'secretary',
  CLUB_SECRETARY: 'Club Secretary',
  SECRETARY_TITLECASE: 'Secretary',
  CO_ASST_SECRETARY: 'Co - Asst. Secretary',
  TREASURER: 'treasurer',
  TREASURER_TITLECASE: 'Treasurer',
  CO_ASST_TREASURER: 'Co - Asst. Treasurer',
  PRESIDENT_PROGRAM_COORDINATOR: 'President Program Coordinator',
  CLUB_PRESIDENT_PROGRAM_COORDINATOR: 'Club President Program Coordinator',
  CLUB_DIRECTOR: 'Club Director',
  ACTIVITY_DIRECTOR: 'Activity Director',
  TEAM_HEAD: 'Team Head',
  IT_DEPARTMENT: 'IT Department',
  CLUB_IT_DEPARTMENT: 'Club IT Department',
  INSTALLATION_SUBCOMMITTEE: 'Installation sub-committee member',
  REPORTING_SUBCOMMITTEE: 'Reporting Sub-Committee',
  CLUB_REPORTING_SUBCOMMITTEE: 'Club Reporting Sub-Committee',
  COMMITTEE_MEMBER: 'Committee Member',
  CLUB_COMMITTEE_MEMBER: 'Club Committee Member',
  CHIEF_EDITOR: 'Chief Editor',
  PAST_PRESIDENT: 'Past President',
  PAST_BOARD_MEMBER: 'Past Board Member',
  CLUB_PERFORMANCE_EVALUATOR: 'Club Performance Evaluator',
  CLUB_PERFORMANCE_EVALUATOR_LEGACY: 'Club Performence Evaluator', // legacy misspelling, still stored on some rows
  CLUB_MEMBER: 'Club Member',
  MEMBER: 'member',
  BANNED: 'Banned',
};

const {
  SYSTEM_ADMIN, PRESIDENT, CLUB_PRESIDENT, PRESIDENT_TITLECASE, VICE_PRESIDENT,
  SECRETARY, CLUB_SECRETARY, SECRETARY_TITLECASE, CO_ASST_SECRETARY,
  TREASURER, TREASURER_TITLECASE, CO_ASST_TREASURER,
  PRESIDENT_PROGRAM_COORDINATOR, CLUB_PRESIDENT_PROGRAM_COORDINATOR,
  CLUB_DIRECTOR, ACTIVITY_DIRECTOR, TEAM_HEAD, IT_DEPARTMENT, CLUB_IT_DEPARTMENT,
  INSTALLATION_SUBCOMMITTEE, REPORTING_SUBCOMMITTEE, CLUB_REPORTING_SUBCOMMITTEE,
  COMMITTEE_MEMBER, CLUB_COMMITTEE_MEMBER, CHIEF_EDITOR, PAST_PRESIDENT,
  PAST_BOARD_MEMBER, CLUB_PERFORMANCE_EVALUATOR, CLUB_PERFORMANCE_EVALUATOR_LEGACY,
  CLUB_MEMBER, MEMBER, BANNED,
} = ROLES;

// Top club officers -- used for the project Secretary/Treasurer report
// feature: only these roles may view, create, update or delete a project's
// Secretary Report or Treasurer Report.
export const PROJECT_REPORT_MANAGER_ROLES = [
  SYSTEM_ADMIN, PRESIDENT, CLUB_PRESIDENT, PRESIDENT_TITLECASE,
  PRESIDENT_PROGRAM_COORDINATOR, CLUB_PRESIDENT_PROGRAM_COORDINATOR,
  VICE_PRESIDENT,
  SECRETARY, CLUB_SECRETARY, SECRETARY_TITLECASE, CO_ASST_SECRETARY,
  TREASURER, TREASURER_TITLECASE, CO_ASST_TREASURER,
];

// Roles Sidebar.jsx shows the "Reporting Hub" nav link to -- kept as its own
// exported list (instead of inline in App.js) so the route guard and the
// sidebar's menu-visibility check can't drift out of sync again.
export const REPORTING_HUB_ROLES = [
  SYSTEM_ADMIN, PRESIDENT, SECRETARY,
  CLUB_PERFORMANCE_EVALUATOR, CLUB_PERFORMANCE_EVALUATOR_LEGACY,
  PRESIDENT_PROGRAM_COORDINATOR, CLUB_DIRECTOR, TREASURER, CLUB_SECRETARY,
  VICE_PRESIDENT, CLUB_PRESIDENT, CLUB_PRESIDENT_PROGRAM_COORDINATOR,
  ACTIVITY_DIRECTOR, TEAM_HEAD, CLUB_IT_DEPARTMENT, INSTALLATION_SUBCOMMITTEE,
  CLUB_REPORTING_SUBCOMMITTEE, REPORTING_SUBCOMMITTEE, CLUB_COMMITTEE_MEMBER,
  CO_ASST_SECRETARY, CO_ASST_TREASURER,
];

export const ADMIN_ROLES = [SYSTEM_ADMIN, PRESIDENT, CLUB_PRESIDENT, PRESIDENT_TITLECASE];

export const OFFICER_ROLES = [
  SYSTEM_ADMIN, PRESIDENT, CLUB_PRESIDENT, PRESIDENT_TITLECASE, VICE_PRESIDENT,
  SECRETARY, CLUB_SECRETARY, SECRETARY_TITLECASE, CO_ASST_SECRETARY,
  TREASURER, TREASURER_TITLECASE, CO_ASST_TREASURER,
  PRESIDENT_PROGRAM_COORDINATOR, CLUB_PRESIDENT_PROGRAM_COORDINATOR,
  CLUB_DIRECTOR, ACTIVITY_DIRECTOR, TEAM_HEAD,
];

// Every non-plain-member role (roughly "has some responsibility in the club").
export const ALL_OFFICER_LIKE_ROLES = [
  SYSTEM_ADMIN, PRESIDENT, CLUB_PRESIDENT, PRESIDENT_TITLECASE, VICE_PRESIDENT,
  SECRETARY, CLUB_SECRETARY, SECRETARY_TITLECASE, CO_ASST_SECRETARY,
  TREASURER, TREASURER_TITLECASE, CO_ASST_TREASURER,
  PRESIDENT_PROGRAM_COORDINATOR, CLUB_PRESIDENT_PROGRAM_COORDINATOR,
  CLUB_DIRECTOR, ACTIVITY_DIRECTOR, TEAM_HEAD,
  IT_DEPARTMENT, CLUB_IT_DEPARTMENT,
  INSTALLATION_SUBCOMMITTEE, REPORTING_SUBCOMMITTEE, CLUB_REPORTING_SUBCOMMITTEE,
  COMMITTEE_MEMBER, CLUB_COMMITTEE_MEMBER, CHIEF_EDITOR,
  CLUB_PERFORMANCE_EVALUATOR, CLUB_PERFORMANCE_EVALUATOR_LEGACY,
];

// Everyone who's ever logged in, including plain members and alumni.
export const EVERYONE_ROLES = [
  ...ALL_OFFICER_LIKE_ROLES,
  PAST_PRESIDENT, PAST_BOARD_MEMBER, CLUB_MEMBER, MEMBER, '',
];

export const BANNED_ROLES = [BANNED];

/** Case-insensitive, whitespace-trimmed role membership check. */
export function hasRole(userRole, allowedRoles) {
  const role = (userRole || '').trim().toLowerCase();
  return (allowedRoles || []).some((r) => (r || '').trim().toLowerCase() === role);
}
